import type { SubscriptionState } from "@repo/stripe/subscription-gate";

/**
 * Map the client-side `SubscriptionState` onto the raw Stripe-mirroring
 * `subscription_status` taxonomy value written to `user.custom` (and stamped on
 * some event metadata). Cancellation is NOT part of the status — it's the
 * separate `cancels_at_period_end` user property (see `cancelsAtPeriodEnd`).
 *
 * `SubscriptionState` is coarse: it collapses `trialing` into "active" (both
 * grant access) and a scheduled cancel into "canceling". Callers pass
 * `isTrialing` (from `useSubscriptionCheck`/`SubscriptionCheckData`) so a trial
 * isn't mislabeled "active". The server webhook is the authoritative source for
 * the precise raw status; this client mapping covers the slice it can see.
 *
 * Returns one of the raw Stripe statuses (`trialing` | `active` | `past_due` |
 * `canceled` | `none`) the client can distinguish, or `undefined` when the
 * caller should not write (loading, or pre-signup — leave what the webhook set).
 *
 * Imported by `StatsigUserPropertiesSync` (writes user.custom) and fire sites
 * that stamp `subscription_status` on event metadata (`home-wizard.tsx`).
 */
export function mapSubscriptionStatusToTaxonomy(
  state: SubscriptionState | undefined,
  isSignedUp: boolean,
  isTrialing = false
): string | undefined {
  // A trial reads as "active"/"canceling" in SubscriptionState; surface it as
  // its own status. Whether it's also set to cancel is the separate flag.
  if (isTrialing && (state === "active" || state === "canceling")) {
    return "trialing";
  }

  switch (state) {
    case "active":
    case "canceling":
      // Still active access; the scheduled cancel is the cancels_at_period_end
      // flag, not a distinct status.
      return "active";
    case "cancelled":
      return "canceled";
    case "payment_issue":
      return "past_due";
    case "unknown":
    case undefined:
      // Signed-up user with no subscription information => none.
      // Pre-signup user => undefined (don't overwrite anything).
      return isSignedUp ? "none" : undefined;
  }
}

/**
 * Whether the user's subscription is scheduled to cancel at period end —
 * carried as a `user.custom.cancels_at_period_end` property, orthogonal to the
 * status. `SubscriptionState` surfaces this as the "canceling" state.
 */
export function cancelsAtPeriodEnd(
  state: SubscriptionState | undefined
): boolean {
  return state === "canceling";
}
